/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * CoordinateCard.tsx
 * Card singola coordinata con:
 * - Colore bordo per tipo (fronte/vedetta/acqua/base)
 * - Badge tipo
 * - Copia lat/lon con un tap
 * - Invia coordinate al form COAU
 * - Nota con input vocale
 */
// src/components/Coordinate/CoordinateCard.tsx
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { MicInput } from '../common/MicInput';
import './CoordinateCard.css';

type CoordTipo = 'fronte' | 'vedetta' | 'risorsa_idrica' | 'base' | 'altro' | '';

interface CoordData {
  id: number;
  lat: string;
  lon: string;
  active: boolean;
  titolo?: string;
  note: string;
  tipo?: CoordTipo;
  capoSquadra: string;
  timestamp: string;
  timestampAssegnazione: string;
}

interface CoordinateCardProps {
  coordinate: CoordData;
  index: number;
  isPosizioneDOS: boolean;
  onToggle: (checked: boolean) => void;
  onUpdateNote: (note: string) => void;
  onDelete: () => void;
}

const TIPO_META: Record<string, { emoji: string; label: string; color: string; bg: string }> = {
  fronte:         { emoji: '🔥', label: 'Fronte',   color: '#ef4444', bg: 'rgba(239,68,68,0.08)' },
  vedetta:        { emoji: '👁',  label: 'Vedetta',  color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  risorsa_idrica: { emoji: '💧', label: 'Acqua',    color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
  base:           { emoji: '🏠', label: 'Base',     color: '#22c55e', bg: 'rgba(34,197,94,0.08)' },
  altro:          { emoji: '📍', label: 'Altro',    color: '#94a3b8', bg: 'transparent' },
};

const getTipoMeta = (tipo?: CoordTipo) =>
  TIPO_META[tipo ?? ''] ?? { emoji: '📍', label: '', color: '#ff6b35', bg: 'transparent' };

export const CoordinateCard: React.FC<CoordinateCardProps> = ({
  coordinate,
  index,
  isPosizioneDOS,
  onToggle,
  onUpdateNote,
  onDelete,
}) => {
  const [showCapoModal, setShowCapoModal]   = useState(false);
  const [copiato, setCopiato]               = useState(false);
  const [inviato, setInviato]               = useState(false);

  const personale      = useAppStore(state => state.personale);
  const coordinate_list= useAppStore(state => state.coordinate);
  const updateCoordinate = useAppStore(state => state.updateCoordinate);
  const updateCOAU     = useAppStore(state => state.updateCOAU);

  const tipo = coordinate.tipo ?? (isPosizioneDOS ? 'altro' : '');
  const meta = getTipoMeta(tipo as CoordTipo);
  const borderColor = isPosizioneDOS ? '#ff6b35' : meta.color;
  const bgColor     = isPosizioneDOS ? 'rgba(255,107,53,0.06)' : meta.bg;

  // Capi squadra
  const capiSquadra = personale.filter(p => p.qualifica === 'capo_squadra');
  const capiGiaAssegnati = new Set<string>();
  coordinate_list.forEach(c => {
    if (String(c.id) !== String(coordinate.id) && c.active && c.capoSquadra?.trim()) {
      capiGiaAssegnati.add(c.capoSquadra.trim());
    }
  });
  const capiDisponibili = capiSquadra.filter(c => {
    const nome = `${c.nome} ${c.cognome}`.trim();
    return nome === coordinate.capoSquadra || !capiGiaAssegnati.has(nome);
  });

  const handleAssegnaCapo = (nomeCompleto: string) => {
    const ora = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    updateCoordinate(coordinate.id as any, {
      ...coordinate,
      capoSquadra: nomeCompleto,
      timestampAssegnazione: nomeCompleto ? ora : '',
    } as any);
    setShowCapoModal(false);
  };

  const handleCopia = async () => {
    try {
      await navigator.clipboard.writeText(`${coordinate.lat}, ${coordinate.lon}`);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1500);
    } catch {
      // Fallback: seleziona testo
    }
  };

  const handleInviaACOAU = () => {
    updateCOAU({ latitudine: coordinate.lat, longitudine: coordinate.lon } as any);
    setInviato(true);
    setTimeout(() => setInviato(false), 2000);
  };

  const nome = coordinate.titolo || `P${index}`;

  return (
    <>
      <div
        className="coordinate-card"
        style={{
          borderLeft: `5px solid ${borderColor}`,
          background: bgColor,
          borderTop: `1px solid ${borderColor}30`,
          borderRight: `1px solid ${borderColor}30`,
          borderBottom: `1px solid ${borderColor}30`,
        }}
      >
        {/* HEADER CARD */}
        <div style={S.cardHeader}>
          <div style={S.cardHeaderLeft}>
            {/* Checkbox attivo */}
            <input
              type="checkbox"
              id={`coord-check-${index}`}
              checked={coordinate.active}
              onChange={e => onToggle(e.target.checked)}
              className="coord-checkbox"
            />
            {/* Nome punto */}
            <label htmlFor={`coord-check-${index}`} style={S.nomePunto}>
              {isPosizioneDOS ? '📍 Posizione DOS' : nome}
            </label>
            {/* Badge tipo */}
            {!isPosizioneDOS && meta.label && (
              <span style={{ ...S.tipoBadge, color: meta.color, borderColor: `${meta.color}50` }}>
                {meta.emoji} {meta.label}
              </span>
            )}
          </div>

          {/* Azioni rapide */}
          <div style={S.quickActions}>
            <button
              style={{ ...S.btnQuick, color: copiato ? '#22c55e' : '#94a3b8' }}
              onClick={handleCopia}
              title="Copia coordinate negli appunti"
            >
              {copiato ? '✓' : '📋'}
            </button>
            {!isPosizioneDOS && (
              <button
                style={{ ...S.btnQuick, color: inviato ? '#22c55e' : '#60a5fa' }}
                onClick={handleInviaACOAU}
                title="Invia al form COAU"
              >
                {inviato ? '✓' : '→ COAU'}
              </button>
            )}
            <button
              style={{ ...S.btnQuick, color: '#ef4444' }}
              onClick={onDelete}
              title="Elimina"
            >
              🗑️
            </button>
          </div>
        </div>

        {/* COORDINATE */}
        <div className="coord-checkbox-row" style={{ marginTop: 8, marginBottom: 6 }}>
          <span className="coord-lat">Lat: {coordinate.lat}</span>
          <span className="coord-separator">, </span>
          <span className="coord-lon">Lon: {coordinate.lon}</span>
        </div>

        <div className="coord-timestamp">
          🕐 {coordinate.timestamp}
        </div>

        {/* CAPO SQUADRA */}
        {!isPosizioneDOS && (
          <div className="coord-capo-section">
            <div className="coord-capo-label">👤 Capo Squadra Assegnato</div>
            <button
              onClick={() => setShowCapoModal(true)}
              className="coord-capo-button"
              style={coordinate.capoSquadra ? { borderColor: '#22c55e', color: '#22c55e' } : undefined}
            >
              {coordinate.capoSquadra || '— Seleziona Capo Squadra —'}
            </button>
            {coordinate.timestampAssegnazione && (
              <div className="coord-assegnato-time">
                ✅ Assegnato alle {coordinate.timestampAssegnazione}
              </div>
            )}
          </div>
        )}

        {/* NOTE con MicInput */}
        <div className="coord-note-section">
          <label className="coord-note-label">💭 Note</label>
          <MicInput
            value={coordinate.note || ''}
            onChange={(e) => onUpdateNote(e.target.value)}
            placeholder={isPosizioneDOS ? 'Note posizione DOS…' : 'Note operazione…'}
            appendMode={false}
          />
        </div>
      </div>

      {/* MODALE CAPO SQUADRA */}
      {showCapoModal && (
        <div className="modal-overlay" onClick={() => setShowCapoModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👤 SELEZIONA CAPO SQUADRA — {nome}</h3>
              <button className="btn-close" onClick={() => setShowCapoModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {capiGiaAssegnati.size > 0 && (
                <div className="info-box warning">
                  <strong>Già assegnati:</strong> {Array.from(capiGiaAssegnati).join(', ')}
                </div>
              )}
              <button
                onClick={() => handleAssegnaCapo('')}
                className={`capo-option ${!coordinate.capoSquadra ? 'selected' : ''}`}
              >
                — Non assegnata —
              </button>
              {capiDisponibili.length === 0 ? (
                <p className="no-data">❌ Nessun capo squadra disponibile</p>
              ) : (
                capiDisponibili.map(capo => {
                  const nome = `${capo.nome} ${capo.cognome}`.trim();
                  return (
                    <button
                      key={capo.id}
                      onClick={() => handleAssegnaCapo(nome)}
                      className={`capo-option ${coordinate.capoSquadra === nome ? 'selected' : ''}`}
                    >
                      👤 {nome}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const S: Record<string, React.CSSProperties> = {
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: 8, marginBottom: 4,
  },
  cardHeaderLeft: {
    display: 'flex', alignItems: 'center', gap: 8, flex: 1, flexWrap: 'wrap',
  },
  nomePunto: {
    fontWeight: 700, fontSize: 15, color: '#fff', cursor: 'pointer',
  },
  tipoBadge: {
    fontSize: 11, fontWeight: 700,
    border: '1px solid', borderRadius: 4,
    padding: '2px 7px', letterSpacing: 0.3,
  },
  quickActions: {
    display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center',
  },
  btnQuick: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 700, padding: '4px 6px',
    borderRadius: 4, transition: 'opacity 0.15s',
  },
};
